import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import LoadingButton from 'atoms/LoadingButton';
import cn from 'classnames';
import { observer } from 'mobx-react-lite';
import FormInputText from 'molecules/FormField/FormInputText';
import NotificationAlert from 'molecules/NotificationAlert';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useStore } from 'stores/store';
import { validateEmail } from 'utils/errorUtils';
import { getSingleValue } from 'utils/urlUtils';

import s from './UnsubscribeForm.module.scss';

interface UnsubscribeFormProps {
  className?: string;
  onResubscribe?: (email: string) => void;
}

interface FormValues {
  email: string;
}

interface EmailUnsubscribePayload {
  email: string;
  token: string;
  system_unsubscribe?: boolean;
  email_unsubscribe?: boolean;
}

const UnsubscribeForm = ({ className, onResubscribe }: UnsubscribeFormProps) => {
  const store = useStore();
  const router = useRouter();
  const [loading, setLoading] = useState({ regular: false, all: false });
  const [unsubscribedEmail, setUnsubscribedEmail] = useState<string>();
  const [notification, setNotification] = useState('');
  const [email, setEmail] = useState<string>();
  const [token, setToken] = useState<string>();
  const [showForm, setShowForm] = useState(false);
  const { register, control, handleSubmit } = useForm<FormValues>({ mode: 'onChange' });

  useEffect(() => {
    if (!router?.isReady) return;

    const queryEmail: string | undefined = getSingleValue(router?.query?.e);
    const queryToken: string | undefined = getSingleValue(router?.query?.t);

    if (queryToken) {
      setToken(queryToken);
    }

    if (queryEmail && validateEmail(queryEmail)) {
      setEmail(queryEmail);
      return;
    }

    setShowForm(true);
    register('email', {
      required: 'Email is required',
      validate: (value) => validateEmail(value) || 'Invalid Email'
    });
  }, [router?.isReady, router?.query, register]);

  const unsubsribeEmail = async (payload: EmailUnsubscribePayload, isRegular = true) => {
    setLoading(isRegular ? { regular: true, all: false } : { regular: false, all: true });
    try {
      await store.ProfileStore.unsubscribeEmail(payload);
      setUnsubscribedEmail(payload.email);
      setShowForm(true);
      if (
        onResubscribe &&
        payload.email_unsubscribe === false &&
        payload.system_unsubscribe === false
      ) {
        onResubscribe(payload.email);
      }
    } catch (err) {
      setNotification(err.message);
      console.warn('Unsubscribe Email - onError', err);
    } finally {
      setLoading({ regular: false, all: false });
    }
  };

  const onSubmitSubscribe: SubmitHandler<FormValues> = async ({ email }) => {
    if (!token) return;
    await unsubsribeEmail({ email, token });
  };

  const resubscribe = async () => {
    if (!token || !unsubscribedEmail) return;
    await unsubsribeEmail({
      email: unsubscribedEmail,
      token,
      system_unsubscribe: false,
      email_unsubscribe: false
    });
  };

  const unsubscribeAll = async () => {
    if (!token) return;
    if (email && token) {
      unsubsribeEmail({ email, token, system_unsubscribe: true, email_unsubscribe: true }, false);
      return;
    }
    handleSubmit(({ email }) =>
      unsubsribeEmail({ email, token, system_unsubscribe: true, email_unsubscribe: true }, false)
    )();
  };

  return (
    <div className={cn(s.UnsubscribeForm, className)}>
      <Typography mb={2} variant="h2">
        We’re sorry to see you go
      </Typography>
      {email && token && !unsubscribedEmail && (
        <Typography mb={2}>
          Please confirm unsubscribing <strong>{email}</strong> <br /> from USREM&apos;s marketing
          emails.
        </Typography>
      )}
      {unsubscribedEmail && (
        <>
          <Typography mb={1}>
            Your request to unsubscribe <strong>[{unsubscribedEmail}]</strong> has been accepted.
          </Typography>
          <Typography mb={3}>
            Please allow up to 24 hours for the request to be processed.
          </Typography>
        </>
      )}
      {showForm && !unsubscribedEmail && (
        <Typography mb={2}>
          Please confirm unsubscribing from USREM&apos;s marketing emails.
        </Typography>
      )}
      <div className={s.UnsubscribeForm__formHolder}>
        {email && token && !unsubscribedEmail && (
          <LoadingButton
            onClick={() => unsubsribeEmail({ email, token })}
            loading={loading.regular}
            disabled={loading.all}
            sx={{ width: '100%' }}
          >
            Unsubscribe
          </LoadingButton>
        )}
        {unsubscribedEmail && (
          <LoadingButton
            onClick={resubscribe}
            loading={loading.regular}
            disabled={loading.all}
            sx={{ width: '100%' }}
            color="secondary"
          >
            I want to resubscribe
          </LoadingButton>
        )}
        {showForm && !unsubscribedEmail && (
          <form onSubmit={handleSubmit(onSubmitSubscribe)}>
            <Box sx={{ mb: 1 }}>
              <FormInputText description="Email Address" name="email" control={control} />
            </Box>
            <LoadingButton
              type="submit"
              loading={loading.regular}
              disabled={loading.all}
              sx={{ width: '100%' }}
            >
              Unsubscribe
            </LoadingButton>
          </form>
        )}
        {!unsubscribedEmail && (
          <Typography mt={3} color="text.secondary" variant="body2">
            If you wish to unsubscribe from ALL emails from USREM platform please click
            <LoadingButton
              onClick={unsubscribeAll}
              sx={{ ml: 0.5 }}
              size="small"
              variant="text"
              type="submit"
              disabled={loading.regular}
              loading={loading.all}
            >
              unsubscribe from all emails.
            </LoadingButton>
          </Typography>
        )}
      </div>
      <NotificationAlert open={!!notification} severity="error" onClose={() => setNotification('')}>
        Error occured: {notification}
      </NotificationAlert>
    </div>
  );
};

export default observer(UnsubscribeForm);
